import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import Footer from '../home/components/Footer';

const LiverProgressionPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // スクロールアニメーション用のコンポーネント
  const FadeInSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-100px' });

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay }}
      >
        {children}
      </motion.div>
    );
  };

  // 自動ステップ進行（5秒間隔に変更）
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    {
      id: 1,
      title: '健康な肝臓',
      subtitle: 'Healthy Liver',
      color: 'from-red-400 to-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      icon: 'ri-heart-pulse-line',
      liverColor: '#8B4513',
      description: '表面は滑らかで、鮮やかな赤褐色。肝細胞が整然と並び、血流がスムーズ。',
      message: '沈黙の臓器として、100%の機能を発揮している状態です。',
      details: [
        '肝細胞が整然と配列',
        '血流が非常にスムーズ',
        '代謝機能が最高レベル',
        '自覚症状は一切なし',
      ],
    },
    {
      id: 2,
      title: '脂肪肝',
      subtitle: 'MASLD (Metabolic Dysfunction-Associated Steatotic Liver Disease)',
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-500',
      icon: 'ri-drop-line',
      liverColor: '#F4D03F',
      description: '摂取エネルギーが消費を上回り、中性脂肪が肝細胞の5%以上に蓄積。肝臓が黄色味を帯び、少し膨らむ（フォアグラ状態）。',
      message: 'まだ逆転可能。しかし、自覚症状は一切ありません。',
      details: [
        '中性脂肪が肝細胞の5%以上に蓄積',
        '肝臓が黄色味を帯びる',
        '肝臓が少し膨らむ',
        '日本の成人男性の約40%、女性の約20%が該当',
      ],
    },
    {
      id: 3,
      title: '炎症と線維化',
      subtitle: 'MASH / Fibrosis',
      color: 'from-orange-400 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-500',
      icon: 'ri-fire-line',
      liverColor: '#E67E22',
      description: '溜まった脂肪が酸化し、炎症が発生。ダメージを修復しようとして、コラーゲン繊維（線維）が溜まり始める。',
      message: 'ここが運命の分岐点！線維化が進むと、元の柔らかさには戻りにくくなります。',
      details: [
        '脂肪の酸化により炎症が発生',
        'コラーゲン繊維が蓄積',
        '肝臓の中に白い筋（線維）が増える',
        '徐々に硬くなっていく',
      ],
    },
    {
      id: 4,
      title: '肝硬変',
      subtitle: 'Cirrhosis',
      color: 'from-red-600 to-red-800',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-700',
      icon: 'ri-error-warning-line',
      liverColor: '#7D3C3C',
      description: '線維が網の目のように張り巡らされ、肝細胞が「再生結節（コブ）」状に孤立。血流が遮断され、機能が著しく低下。',
      message: '肝がんのリスクが激増。手遅れになる前に発見が必要です。',
      details: [
        '線維が網の目のように張り巡らされる',
        '肝細胞が再生結節（コブ）状に孤立',
        '血流が遮断され、機能が著しく低下',
        '表面がボコボコになり、全体的に小さく硬く縮む',
      ],
    },
  ];

  // 肝臓のビジュアルコンポーネント
  const LiverVisual = ({ step }: { step: number }) => {
    const currentStep = steps[step];
    
    return (
      <motion.div
        key={step}
        initial={{ opacity: 0, scale: 0.8, rotateY: 90 }}
        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        exit={{ opacity: 0, scale: 0.8, rotateY: -90 }}
        transition={{ 
          duration: 0.6,
          type: "spring",
          stiffness: 100
        }}
        className="relative w-full h-64 flex items-center justify-center"
      >
        {/* STEP 1: 健康な肝臓 */}
        {step === 0 && (
          <motion.div
            animate={{ 
              scale: [1, 1.02, 1],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative"
          >
            <svg width="280" height="200" viewBox="0 0 280 200">
              <defs>
                <linearGradient id="healthyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#C17B6B', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#A0522D', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#8B4513', stopOpacity: 1 }} />
                </linearGradient>
                <filter id="shadow">
                  <feDropShadow dx="0" dy="4" stdDeviation="3" floodOpacity="0.3"/>
                </filter>
              </defs>
              {/* 肝臓の形状 - 右葉（大きい方） */}
              <path
                d="M 140 40 
                   Q 200 35 240 60 
                   Q 265 80 268 110 
                   Q 268 140 250 165 
                   Q 230 185 200 190 
                   Q 170 193 140 185 
                   L 140 40 Z"
                fill="url(#healthyGradient)"
                stroke="#6B3410"
                strokeWidth="3"
                filter="url(#shadow)"
              />
              {/* 肝臓の形状 - 左葉（小さい方） */}
              <path
                d="M 140 40 
                   Q 100 38 70 55 
                   Q 42 69 38 95 
                   Q 36 122 48 148 
                   Q 63 168 88 178 
                   Q 113 186 140 185 
                   L 140 40 Z"
                fill="url(#healthyGradient)"
                stroke="#6B3410"
                strokeWidth="3"
                filter="url(#shadow)"
              />
              {/* 中央の溝 */}
              <line
                x1="140"
                y1="40"
                x2="140"
                y2="185"
                stroke="#6B3410"
                strokeWidth="2"
                opacity="0.5"
              />
              {/* 健康的な輝き */}
              <motion.ellipse
                cx="100"
                cy="80"
                rx="25"
                ry="20"
                fill="white"
                opacity="0.25"
                animate={{ opacity: [0.2, 0.35, 0.2] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <motion.ellipse
                cx="200"
                cy="90"
                rx="30"
                ry="25"
                fill="white"
                opacity="0.25"
                animate={{ opacity: [0.2, 0.35, 0.2] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              />
            </svg>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-sm font-bold text-gray-700">
              滑らかで健康的
            </div>
          </motion.div>
        )}

        {/* STEP 2: 脂肪肝 */}
        {step === 1 && (
          <motion.div
            animate={{ 
              scale: [1.05, 1.07, 1.05],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative"
          >
            <svg width="280" height="200" viewBox="0 0 280 200">
              <defs>
                <linearGradient id="fattyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#F9E79F', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#F4D03F', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#F39C12', stopOpacity: 1 }} />
                </linearGradient>
              </defs>
              {/* 肝臓の形状 - 右葉（膨らんだ状態） */}
              <path
                d="M 140 35 
                   Q 205 30 245 58 
                   Q 272 78 275 110 
                   Q 275 145 255 172 
                   Q 232 193 200 198 
                   Q 165 200 140 190 
                   L 140 35 Z"
                fill="url(#fattyGradient)"
                stroke="#D4AC0D"
                strokeWidth="3"
                filter="url(#shadow)"
              />
              {/* 肝臓の形状 - 左葉（膨らんだ状態） */}
              <path
                d="M 140 35 
                   Q 95 33 65 52 
                   Q 38 68 33 95 
                   Q 30 125 45 152 
                   Q 62 173 90 183 
                   Q 115 190 140 190 
                   L 140 35 Z"
                fill="url(#fattyGradient)"
                stroke="#D4AC0D"
                strokeWidth="3"
                filter="url(#shadow)"
              />
              {/* 中央の溝 */}
              <line
                x1="140"
                y1="35"
                x2="140"
                y2="190"
                stroke="#D4AC0D"
                strokeWidth="2"
                opacity="0.5"
              />
              {/* 脂肪滴 - 右葉 */}
              {[...Array(12)].map((_, i) => (
                <motion.circle
                  key={`right-${i}`}
                  cx={160 + (i % 4) * 25}
                  cy={70 + Math.floor(i / 4) * 30}
                  r="5"
                  fill="#FFF9C4"
                  opacity="0.8"
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.6, 0.9, 0.6]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.15
                  }}
                />
              ))}
              {/* 脂肪滴 - 左葉 */}
              {[...Array(8)].map((_, i) => (
                <motion.circle
                  key={`left-${i}`}
                  cx={60 + (i % 3) * 25}
                  cy={75 + Math.floor(i / 3) * 30}
                  r="5"
                  fill="#FFF9C4"
                  opacity="0.8"
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.6, 0.9, 0.6]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.15
                  }}
                />
              ))}
            </svg>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-sm font-bold text-gray-700">
              黄色く膨らんだ状態
            </div>
          </motion.div>
        )}

        {/* STEP 3: 炎症と線維化 */}
        {step === 2 && (
          <motion.div
            animate={{ 
              scale: [1, 1.01, 1],
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative"
          >
            <svg width="280" height="200" viewBox="0 0 280 200">
              <defs>
                <linearGradient id="fibrosisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#F39C12', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#E67E22', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#D35400', stopOpacity: 1 }} />
                </linearGradient>
              </defs>
              {/* 肝臓の形状 - 右葉 */}
              <path
                d="M 140 38 
                   Q 200 33 242 58 
                   Q 268 78 270 110 
                   Q 270 142 252 168 
                   Q 232 188 200 193 
                   Q 168 196 140 188 
                   L 140 38 Z"
                fill="url(#fibrosisGradient)"
                stroke="#CA6F1E"
                strokeWidth="3"
                filter="url(#shadow)"
              />
              {/* 肝臓の形状 - 左葉 */}
              <path
                d="M 140 38 
                   Q 98 36 68 53 
                   Q 42 69 38 95 
                   Q 36 122 48 148 
                   Q 63 168 88 178 
                   Q 113 186 140 188 
                   L 140 38 Z"
                fill="url(#fibrosisGradient)"
                stroke="#CA6F1E"
                strokeWidth="3"
                filter="url(#shadow)"
              />
              {/* 中央の溝 */}
              <line
                x1="140"
                y1="38"
                x2="140"
                y2="188"
                stroke="#CA6F1E"
                strokeWidth="2"
                opacity="0.5"
              />
              {/* 線維（白い筋） - 右葉 */}
              {[...Array(8)].map((_, i) => (
                <motion.line
                  key={`fiber-right-${i}`}
                  x1={155 + i * 15}
                  y1={50}
                  x2={160 + i * 15}
                  y2={180}
                  stroke="white"
                  strokeWidth="2.5"
                  opacity="0.6"
                  animate={{ opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
              {/* 線維（白い筋） - 左葉 */}
              {[...Array(5)].map((_, i) => (
                <motion.line
                  key={`fiber-left-${i}`}
                  x1={55 + i * 15}
                  y1={60}
                  x2={60 + i * 15}
                  y2={170}
                  stroke="white"
                  strokeWidth="2.5"
                  opacity="0.6"
                  animate={{ opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
              {/* 炎症部分（赤い点） */}
              {[...Array(10)].map((_, i) => (
                <motion.circle
                  key={`inflammation-${i}`}
                  cx={70 + (i % 5) * 35}
                  cy={80 + Math.floor(i / 5) * 50}
                  r="4"
                  fill="#E74C3C"
                  animate={{ 
                    scale: [1, 1.6, 1],
                    opacity: [0.6, 1, 0.6]
                  }}
                  transition={{ 
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.15
                  }}
                />
              ))}
            </svg>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-sm font-bold text-gray-700">
              線維と炎症が進行
            </div>
          </motion.div>
        )}

        {/* STEP 4: 肝硬変 */}
        {step === 3 && (
          <motion.div
            animate={{ 
              scale: [0.9, 0.91, 0.9],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative"
          >
            <svg width="280" height="200" viewBox="0 0 280 200">
              <defs>
                <linearGradient id="cirrhosisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style={{ stopColor: '#943126', stopOpacity: 1 }} />
                  <stop offset="50%" style={{ stopColor: '#7D3C3C', stopOpacity: 1 }} />
                  <stop offset="100%" style={{ stopColor: '#5D1F1A', stopOpacity: 1 }} />
                </linearGradient>
              </defs>
              {/* 肝臓の形状 - 右葉（縮んで変形） */}
              <path
                d="M 140 50 
                   Q 190 48 225 68 
                   Q 248 85 252 110 
                   Q 250 135 235 155 
                   Q 215 172 190 178 
                   Q 165 182 140 175 
                   L 140 50 Z"
                fill="url(#cirrhosisGradient)"
                stroke="#5D1F1A"
                strokeWidth="4"
                filter="url(#shadow)"
              />
              {/* 肝臓の形状 - 左葉（縮んで変形） */}
              <path
                d="M 140 50 
                   Q 105 48 80 63 
                   Q 58 78 55 100 
                   Q 53 122 63 142 
                   Q 78 160 100 170 
                   Q 120 177 140 175 
                   L 140 50 Z"
                fill="url(#cirrhosisGradient)"
                stroke="#5D1F1A"
                strokeWidth="4"
                filter="url(#shadow)"
              />
              {/* 中央の溝 */}
              <line
                x1="140"
                y1="50"
                x2="140"
                y2="175"
                stroke="#5D1F1A"
                strokeWidth="3"
                opacity="0.7"
              />
              {/* 再生結節（コブ） - 右葉 */}
              {[...Array(12)].map((_, i) => (
                <circle
                  key={`nodule-right-${i}`}
                  cx={160 + (i % 4) * 22}
                  cy={75 + Math.floor(i / 4) * 28}
                  r="9"
                  fill="#A04030"
                  stroke="#5D1F1A"
                  strokeWidth="1.5"
                  opacity="0.85"
                />
              ))}
              {/* 再生結節（コブ） - 左葉 */}
              {[...Array(8)].map((_, i) => (
                <circle
                  key={`nodule-left-${i}`}
                  cx={70 + (i % 3) * 22}
                  cy={80 + Math.floor(i / 3) * 28}
                  r="9"
                  fill="#A04030"
                  stroke="#5D1F1A"
                  strokeWidth="1.5"
                  opacity="0.85"
                />
              ))}
              {/* 線維の網目 - 縦線 */}
              {[...Array(10)].map((_, i) => (
                <line
                  key={`grid-v-${i}`}
                  x1={60 + i * 18}
                  y1={55}
                  x2={60 + i * 18}
                  y2={170}
                  stroke="white"
                  strokeWidth="1.5"
                  opacity="0.35"
                />
              ))}
              {/* 線維の網目 - 横線 */}
              {[...Array(6)].map((_, i) => (
                <line
                  key={`grid-h-${i}`}
                  x1={60}
                  y1={70 + i * 20}
                  x2={240}
                  y2={70 + i * 20}
                  stroke="white"
                  strokeWidth="1.5"
                  opacity="0.35"
                />
              ))}
            </svg>
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-sm font-bold text-gray-700">
              硬く縮んで変形
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">Liver Health Check</span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            脂肪肝から肝硬変への<br />
            進行リスク
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            肝臓は「沈黙の臓器」と呼ばれ、自覚症状がないまま病気が進行します。<br />
            4つのステップで、その進行リスクを理解しましょう。
          </p>
        </motion.div>

        {/* インタラクティブステップ表示 */}
        <FadeInSection>
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">進行の4ステップ</h2>
            
            {/* ステップナビゲーション */}
            <div className="flex items-center justify-center gap-4 mb-12 flex-wrap">
              {steps.map((step, index) => (
                <motion.button
                  key={step.id}
                  onClick={() => setActiveStep(index)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-6 py-3 rounded-full font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeStep === index
                      ? `bg-gradient-to-r ${step.color} text-white shadow-lg`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  STEP {step.id}
                </motion.button>
              ))}
            </div>

            {/* アクティブステップの詳細 - AnimatePresenceで切り替えアニメーション */}
            <div className="relative min-h-[500px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -50, scale: 0.95 }}
                  transition={{ 
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                  className={`p-8 ${steps[activeStep].bgColor} rounded-2xl border-l-4 ${steps[activeStep].borderColor}`}
                >
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* 左側：肝臓のビジュアル */}
                    <div className="flex items-center justify-center">
                      <AnimatePresence mode="wait">
                        <LiverVisual step={activeStep} />
                      </AnimatePresence>
                    </div>

                    {/* 右側：説明文 */}
                    <div className="flex flex-col justify-center">
                      <div className="flex items-start gap-4 mb-4">
                        <motion.div 
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ 
                            delay: 0.2,
                            type: "spring",
                            stiffness: 200,
                            damping: 15
                          }}
                          className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${steps[activeStep].color} rounded-full flex-shrink-0`}
                        >
                          <i className={`${steps[activeStep].icon} text-white text-3xl`}></i>
                        </motion.div>
                        <div className="flex-1">
                          <motion.h3 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-2xl font-bold text-gray-900 mb-2"
                          >
                            {steps[activeStep].title}
                          </motion.h3>
                          <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15 }}
                            className="text-xs text-gray-600 mb-3 italic"
                          >
                            {steps[activeStep].subtitle}
                          </motion.p>
                        </div>
                      </div>
                      <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-sm text-gray-700 mb-4 leading-relaxed"
                      >
                        {steps[activeStep].description}
                      </motion.p>
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.25 }}
                        className="p-4 bg-white rounded-xl mb-4"
                      >
                        <p className="text-base font-bold text-gray-900">{steps[activeStep].message}</p>
                      </motion.div>
                      <motion.ul 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="space-y-2"
                      >
                        {steps[activeStep].details.map((detail, idx) => (
                          <motion.li 
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.35 + idx * 0.05 }}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
                            <span>{detail}</span>
                          </motion.li>
                        ))}
                      </motion.ul>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* プログレスバー */}
            <div className="mt-8 flex items-center gap-2">
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  animate={{
                    backgroundColor: index === activeStep ? '#2563eb' : '#e5e7eb',
                    scale: index === activeStep ? 1.05 : 1
                  }}
                  transition={{ duration: 0.3 }}
                  className="h-2 flex-1 rounded-full"
                ></motion.div>
              ))}
            </div>
          </div>
        </FadeInSection>

        {/* 日本の脂肪肝の現状データ */}
        <FadeInSection delay={0.2}>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg border-2 border-blue-200 mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full flex-shrink-0">
                <i className="ri-bar-chart-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">日本の脂肪肝の現状</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <motion.div
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.3 }}
                className="p-6 bg-white rounded-xl border border-blue-200"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">40%</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">成人男性の推計患者数</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  日本の成人男性の約40%が脂肪肝（MASLD）と言われています。
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.3 }}
                className="p-6 bg-white rounded-xl border border-blue-200"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">20%</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">成人女性の推計患者数</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  日本の成人女性の約20%が脂肪肝（MASLD）と言われています。
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.3 }}
                className="p-6 bg-white rounded-xl border border-blue-200"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-full mb-3">
                  <i className="ri-alert-line text-white text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">「非肥満」の恐怖</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  日本人は欧米人に比べインスリン分泌能力が低いため、太っていなくても（BMIが普通でも）脂肪肝になるリスクが高いのが特徴です。
                </p>
              </motion.div>
            </div>
          </div>
        </FadeInSection>

        {/* FIB-4の重要性 */}
        <FadeInSection delay={0.4}>
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full flex-shrink-0">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">FIB-4インデックスの重要性</h2>
            </div>

            <p className="text-base text-gray-700 mb-6 leading-relaxed">
              血液データだけで「STEP 3：炎症と線維化」への進行度を予測できるため、健診データ活用は<strong className="text-blue-600">「命を救う最初の一歩」</strong>になります。
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.3 }}
                className="p-6 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full">
                    <i className="ri-test-tube-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">血液データだけで予測</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  年齢、AST、ALT、血小板数の4つの指標だけで、肝線維化のリスクを予測できます。
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.3 }}
                className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">早期発見が可能</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  自覚症状がない段階で、肝線維化の進行を早期に発見できます。
                </p>
              </motion.div>
            </div>
          </div>
        </FadeInSection>

        {/* 重要な注意事項 */}
        <FadeInSection delay={0.6}>
          <div className="bg-blue-50 rounded-2xl p-8 border-l-4 border-blue-500 mb-16">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full flex-shrink-0">
                <i className="ri-information-line text-white text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">重要な注意事項</h3>
                <ul className="space-y-2 text-sm text-gray-700 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                    <span>本サービスは医療行為・診断・治療を行うものではありません。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                    <span>体調不良や不安がある場合は、必ず医師・医療機関にご相談ください。</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                    <span>定期的な健康診断と、医師の指導に基づく健康管理が重要です。</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection delay={0.8}>
          <div className="text-center">
            <motion.button
              onClick={() => navigate('/check')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-12 py-6 bg-gradient-to-r from-blue-600 to-green-600 text-white text-xl font-bold rounded-full hover:from-blue-700 hover:to-green-700 transition-all duration-300 shadow-2xl whitespace-nowrap cursor-pointer"
            >
              自分の数値を計算してみる
            </motion.button>
            <p className="text-sm text-gray-600 mt-4">
              無料・匿名で今すぐチェックできます
            </p>
          </div>
        </FadeInSection>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LiverProgressionPage;
